<template>
  <div class="m-filter-modal">
    <div class="m-filter-header">
      <div
        class="arrow"
        @click="toggleMobileFilterVisibility(false)"
      >
        <LongArrowLeftIcon class="m-filter-header-icon" />
      </div>
    </div>
    <div class="m-filter-body scrollbar">
      <div
        class="m-filter-planame"
        v-if="showPriceRangeMobileFilter && !planPriceExsist"
      >
        <h3 class="m-filter-planame-title">Price Range</h3>
        <RangeSlider
          :min="minPriceRangeMobileFilter"
          :max="maxPriceRangeMobileFilter"
          :showMinRangeOnSlider="showMinPriceRangeMobileFilter"
          :showMaxRangeOnSlider="showMaxPriceRangeMobileFilter"
          @updatePriceRangeMobile="(payload) => emit('updatePriceRangeMobile', payload)"
        />
      </div>
      <div class="m-filter-planame relative planName">
        <div
          class="fw-ic-jb m-planame-btn"
          @click="showPlanName = !showPlanName"
        >
          <h3 class="m-filter-planame-title">Plan Name</h3>
          <i class="m-planame-btn-icon">
            <ArrowDownIcon :class="{ 'rotate-180': showPlanName }" />
          </i>
        </div>
        <div :class="['dropdown m-filter-planame-list', { show: showPlanName }]">
          <div class="form-check">
            <input
              type="checkbox"
              id="all"
              class="form-check-input"
              :value="allPlansSelected"
              v-model="allPlansSelected"
              @click="selectAllPlans"
            />
            <label
              for="all"
              class="form-check-label"
              >Select All</label
            >
          </div>
          <div
            v-for="planName in planNameQmiList"
            :key="planName.plan_id"
            class="form-check"
          >
            <input
              type="checkbox"
              :id="planName.plan_id"
              class="form-check-input"
              :value="planName.plan_name"
              v-model="listPlanName"
            />
            <label
              :for="planName.plan_id"
              class="form-check-label"
              >{{ planName.plan_name }}</label
            >
          </div>
        </div>
      </div>
      <div
        class="m-filter-planame"
        v-if="homeTypeCount > 1"
      >
        <h3 class="m-filter-planame-title">Home Type</h3>
        <div class="m-filter-planame-list homeType">
          <div
            v-for="(homeType, index) in homeTypeList"
            :key="index"
            class="form-check"
          >
            <input
              type="checkbox"
              :value="homeType"
              :id="index"
              class="form-check-input"
              v-model="listHomeType"
            />
            <label
              :for="index"
              class="form-check-label"
              >{{ homeType }}</label
            >
          </div>
        </div>
      </div>
      <div class="m-filter-planame">
        <h3 class="m-filter-planame-title">Quick Move-in</h3>
        <div class="m-filter-planame-list move-in-days">
          <span
            v-for="(option, index) in qmiDaysFilter"
            :key="index"
            :class="['daysFilter', { active: qmiFilterList.some((opt) => opt.value == option.value) }]"
            :value="option.value"
            :id="option.value"
            @click="handleQmiDaysClick"
            >{{ option.value }}</span
          >
        </div>
      </div>
    </div>
    <div class="m-filter-footer">
      <div class="cta__btn">
        <button
          class="cta__btntext"
          @click="clearFilters"
        >
          Clear
        </button>
        <button
          class="cta__btnsolid"
          @click="applyFilters"
          :class="[{ disableApply: disableApply }]"
        >
          Apply
        </button>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted, toRefs, watch, computed } from 'vue';
import LongArrowLeftIcon from '@/assets/icons/LongArrowLeftIcon.vue';
import ArrowDownIcon from '@/assets/icons/ArrowDown.vue';
import RangeSlider from './RangeSlider.vue';
import { QmiDaysFilter } from '@/constant';
import { CommunityQmiDetails, QmiDays, SidebarHomePlan, planDetails } from '@/types/sidebar';

const props = defineProps<{
  planPriceExsist: boolean;
  listHomeTypeMobileFilter: string[];
  listPlanNameMobileFilter: string[];
  qmiFilterListMobileFilter: QmiDays[];
  communityHomePlans: SidebarHomePlan[];
  homeTypeList: Set<string>;
  selectedNeighborhoodIds: string[];
  minPriceRangeMobileFilter: number;
  maxPriceRangeMobileFilter: number;
  showMinPriceRangeMobileFilter: number;
  showMaxPriceRangeMobileFilter: number;
  showPriceRangeMobileFilter: boolean;
  qmiDetails: CommunityQmiDetails;
  enableMobileApply: boolean;
  homeTypeCount: number;
}>();

const { planPriceExsist, listHomeTypeMobileFilter, listPlanNameMobileFilter, qmiFilterListMobileFilter, communityHomePlans, homeTypeList, minPriceRangeMobileFilter, maxPriceRangeMobileFilter, showMinPriceRangeMobileFilter, showMaxPriceRangeMobileFilter, showPriceRangeMobileFilter, qmiDetails, enableMobileApply, homeTypeCount } = toRefs(props);

const emit = defineEmits<{
  (e: 'toggleMobileFilterModalVisibility', show: boolean): void;
  (
    e: 'applyMobileFilters',
    payload: {
      listPlanName: string[];
      listHomeType: string[];
      qmiFilterList: QmiDays[];
    }
  ): void;
  (e: 'clearMobileFilters'): void;
  (e: 'updatePriceRangeMobile', payload: { minValue: number; maxValue: number }): void;
  (e: 'checkHomeTypeCount'): void;
}>();

const listHomeType = ref<string[]>([]);
const listPlanName = ref<string[]>([]);
const qmiFilterList = ref<QmiDays[]>([]);
const allPlansSelected = ref(false);
const showPlanName = ref(false);
const disableApply = ref(true);
const isPriceApplied = ref(false);
const planNameQmiList = ref<planDetails[]>([]);
const qmiDaysFilter = QmiDaysFilter;

const changeFilters = computed(() => !!listHomeType.value.length || !!listPlanName.value.length || !!qmiFilterList.value.length);

watch(listPlanName, () => {
  allPlansSelected.value = listPlanName.value.length === planNameQmiList.value.length;
});

watch([changeFilters, isPriceApplied], () => {
  disableApply.value = !(changeFilters.value || isPriceApplied.value);
});

watch(enableMobileApply, (val) => {
  disableApply.value = !val;
  isPriceApplied.value = val;
});

onMounted(() => {
  listHomeType.value = [...listHomeTypeMobileFilter.value];
  listPlanName.value = [...listPlanNameMobileFilter.value];
  qmiFilterList.value = [...qmiFilterListMobileFilter.value];

  if (listPlanName.value.length === communityHomePlans.value.length) {
    allPlansSelected.value = true;
  }

  const planId: string[] = [];
  communityHomePlans.value.forEach((homePlan) => {
    planNameQmiList.value.push({
      plan_id: homePlan.plan_id,
      plan_name: homePlan.plan_name,
    });
    planId.push(homePlan.plan_id);
  });

  for (const key of Object.keys(qmiDetails.value)) {
    qmiDetails.value[key].forEach((qmi) => {
      if (!planId.includes(qmi.plan_code)) {
        planNameQmiList.value.push({
          plan_id: qmi.plan_code,
          plan_name: qmi.plan_market_name,
        });
      }
    });
  }

  window.addEventListener('click', hideDropDown);
});

const hideDropDown = (e: Event) => {
  const planNameEl = document.querySelector('.planName');
  if (planNameEl && !planNameEl.contains(e.target as Node)) {
    showPlanName.value = false;
  }
};

const selectAllPlans = () => {
  listPlanName.value = [];
  if (!allPlansSelected.value) {
    planNameQmiList.value.forEach((plan) => {
      listPlanName.value.push(plan.plan_name);
    });
  }
  allPlansSelected.value = !allPlansSelected.value;
};

const applyFilters = () => {
  emit('applyMobileFilters', {
    listPlanName: listPlanName.value,
    listHomeType: listHomeType.value,
    qmiFilterList: qmiFilterList.value,
  });
  emit('toggleMobileFilterModalVisibility', false);
};

const clearFilters = () => {
  emit('toggleMobileFilterModalVisibility', false);
  emit('clearMobileFilters');
};

const toggleMobileFilterVisibility = (value) => {
  emit('toggleMobileFilterModalVisibility', value);
};

const handleQmiDaysClick = (event: MouseEvent) => {
  const target = event.currentTarget as HTMLElement;
  const qmiDaysValue = target.getAttribute('value');
  if (!qmiDaysValue) return;

  const qmiDay = qmiDaysFilter.find((element) => element.value === qmiDaysValue);
  if (!qmiDay) return;

  const index = qmiFilterList.value.findIndex((opt) => opt.value == qmiDay.value);
  if (index >= 0) {
    qmiFilterList.value = qmiFilterList.value.filter((opt) => opt.value !== qmiDay.value);
  } else {
    qmiFilterList.value = [...qmiFilterList.value, qmiDay];
  }
};
</script>

<style lang="scss">
.m-filter-modal {
  position: fixed;
  background: $white;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 1007;
  display: flex;
  flex-direction: column;
}

.m-filter-header {
  padding: 20px 20px 12px;

  &-icon {
    --size: 48px;
    color: $black;
    width: var(--size);
    height: var(--size);
  }
}

.m-filter-body {
  flex: 1;
  overflow: auto;
  padding-inline: 20px;

  .range__slider__container {
    padding: 0;
  }

  .range__slider__minmax {
    margin-top: 4px;
    gap: 20px;
  }

  .range__slider__minmaxtitle {
    margin-bottom: 24px;
    @include font(12px, 400);
    color: #000;
    margin-inline: 8px;
  }

  .range__slider__input {
    height: 38px;
    @include font(14px, 400);
    color: #000;
  }
}

.m-filter-planame {
  padding-bottom: 20px;
  margin-bottom: 16px;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(53, 53, 53, 0.08);
  }

  &-title {
    color: $black;
    @include font(14px, 600);
    margin-bottom: 20px;
  }

  &-list {
    display: grid;
    column-gap: 20px;
    row-gap: 17px;
    border-radius: 4px;

    &:not(.homeType) {
      padding: 14px;
      top: calc(100% - 12px);
      width: 100%;
      max-height: 195px;
    }

    .form-check {
      --flexGap: 9px;
    }

    &.move-in-days {
      display: flex;
      flex-wrap: wrap;
    }

    .form-check {
      --flexGap: 18px;

      &-label {
        @include font(12px, 400);
        color: $gray-900;
      }
    }
  }
}

.m-filter-footer {
  background: #f8fafb;
  box-shadow: -4px 0px 4px 0px rgba(0, 0, 0, 0.25);

  .cta__btn {
    justify-content: space-between;
    padding: 14px 20px;

    button {
      font-size: 12px;
      min-height: 32px;

      &:not(.cta__btntext) {
        min-width: 126px;
        font-weight: 600;
      }

      &.cta__btntext {
        padding-inline: 0;
      }
    }
  }
}

.m-planame-btn {
  box-shadow: 0px 2px 3px 0px rgba(163, 131, 63, 0.13);
  border-radius: 8px;
  padding: 7px 14px;
  cursor: pointer;

  .m-filter-planame-title {
    margin-bottom: 0;
  }

  &-icon {
    width: 29px;
    height: 25px;
    display: grid;
    place-content: center;
    color: $gray-600;
  }
}

.daysFilter {
  border: 1px solid rgba(53, 53, 53, 0.3);
  border-radius: 100vmax;
  padding: 9px 15px;
  text-align: center;
  color: $gray-600;
  @include font(12px, 600);
  cursor: pointer;

  &.active {
    background-color: $lime-green-new;
    color: $white;
    border-color: $lime-green-new;
  }
}

.disableApply {
  opacity: 0.5;
  pointer-events: none;
}

.arrow {
  display: inline-block;
}
</style>
